import { Injectable } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';
import { catchError, of, tap } from 'rxjs';
import {
  CollectedServiceService,
  CustomerService,
} from 'src/app/generated/backend/services';
import { LoadCollectedServicesAction } from 'src/app/state/collected-services/collected-services.actions';
import { mapCustomerDto } from 'src/app/utils/customer.utils';
import {
  DeleteBookingAction,
  LoadBookingClientAction,
  ResetBookingAction,
  SubmitBookingAction,
  UpdateBookingAction,
} from './booking.actions';
import { BookingStateModel } from './booking.state-model';

export const bookingStateDefaults: BookingStateModel = {
  client: null,
  submitError: false,
};

@State<BookingStateModel>({
  name: 'booking',
  defaults: bookingStateDefaults,
})
@Injectable()
export class BookingState {
  constructor(
    private customerService: CustomerService,
    private collectedService: CollectedServiceService,
  ) {}

  @Action(LoadBookingClientAction)
  loadClientBookingData(
    ctx: StateContext<BookingStateModel>,
    { customerId, collectedServiceDate }: LoadBookingClientAction,
  ) {
    return this.customerService
      .apiCustomerCustomerIdCollectedServiceDateGet$Json({
        customerId,
        collectedServiceDate,
      })
      .pipe(
        tap((response) => {
          ctx.patchState({
            client: mapCustomerDto(response),
            submitError: false,
          });
        }),
      );
  }

  @Action(SubmitBookingAction)
  submitBooking(
    ctx: StateContext<BookingStateModel>,
    { collectedService }: SubmitBookingAction,
  ) {
    return this.collectedService
      .apiCollectedServicePost$Json({
        body: collectedService,
      })
      .pipe(
        catchError((error) => {
          console.error(error);
          return of(null);
        }),
        tap((response) => {
          if (response === null) {
            ctx.patchState({ submitError: true });
          } else {
            ctx.patchState({ submitError: false });
            ctx.dispatch(
              new LoadBookingClientAction(
                collectedService.customerId as number,
                collectedService.startDate as string,
              ),
            );
            ctx.dispatch(new LoadCollectedServicesAction());
          }
        }),
      );
  }

  @Action(UpdateBookingAction)
  updateBooking(
    ctx: StateContext<BookingStateModel>,
    { collectedService }: UpdateBookingAction,
  ) {
    return this.collectedService
      .apiCollectedServicePut$Json({
        body: collectedService,
      })
      .pipe(
        catchError((error) => {
          console.error(error);
          return of(null);
        }),
        tap((response) => {
          if (response === null) {
            ctx.patchState({ submitError: true });
          } else {
            ctx.patchState({ submitError: false });
            ctx.dispatch(
              new LoadBookingClientAction(
                collectedService.customerId as number,
                collectedService.startDate as string,
              ),
            );
            ctx.dispatch(new LoadCollectedServicesAction());
          }
        }),
      );
  }

  @Action(DeleteBookingAction)
  deleteBookingAction(
    ctx: StateContext<BookingStateModel>,
    { bookingId, startDate }: DeleteBookingAction,
  ) {
    return this.collectedService
      .apiCollectedServiceDelete({
        CollectedServiceId: bookingId,
      })
      .pipe(
        catchError((error) => {
          console.error(error);
          return of(null);
        }),
        tap((response) => {
          if (response === null) {
            ctx.patchState({ submitError: true });
          } else {
            ctx.patchState({ submitError: false });
            ctx.dispatch(
              new LoadBookingClientAction(
                ctx.getState().client?.id as number,
                startDate,
              ),
            );
            ctx.dispatch(new LoadCollectedServicesAction());
          }
        }),
      );
  }

  @Action(ResetBookingAction)
  resetBookingAction(ctx: StateContext<BookingStateModel>) {
    ctx.setState(bookingStateDefaults);
  }
}
